import { CheckCircle, CircleDashed, Circle, Zap } from 'lucide-react'
import { Link } from 'react-router-dom'

// ─── Derive docs base URL from the support API env var ───────────────────────
// VITE_SUPPORT_API_URL = https://support.{env}.api.whiteboardfederalrewired.com
// Swagger docs live at    https://{env}.api.whiteboardfederalrewired.com
const DOCS_BASE_URL = (() => {
  const supportUrl = import.meta.env.VITE_SUPPORT_API_URL as string | undefined
  if (!supportUrl) return 'https://api.whiteboardfederalrewired.com'
  try {
    const hostname = new URL(supportUrl).hostname
    const dot = hostname.indexOf('.')
    if (dot !== -1) return `https://${hostname.slice(dot + 1)}`
  } catch { /* fall through */ }
  return 'https://api.whiteboardfederalrewired.com'
})()

// ─── Feature status ───────────────────────────────────────────────────────────

type FeatureStatus = 'done' | 'in-progress' | 'not-started'

interface FeatureRow {
  feature: string
  status: FeatureStatus
  ui: string
  backend: string
  uiLink?: string
  backendLink?: string
}

interface FeatureGroup {
  group: string
  rows: FeatureRow[]
}

const FEATURE_GROUPS: FeatureGroup[] = [
  {
    group: 'Platform',
    rows: [
      { feature: 'Authentication (OIDC)',       status: 'done',         ui: 'Complete',                    backend: 'Cognito — live' },
      { feature: 'Visual Design System',        status: 'done',         ui: 'Complete',                    backend: 'N/A' },
      { feature: 'Metrics',                     status: 'done',         ui: 'Complete',                    backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
    ],
  },
  {
    group: 'Admin Area',
    rows: [
      { feature: 'Layout Studio — New Layout',    status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'Layout Studio',                 status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'Production Desk — Page Editor', status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'Production Desk',               status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'Copy Desk — Article Editor',    status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'Copy Desk',                     status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'Publication Scheduler',         status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
    ],
  },
  {
    group: 'Reader Area',
    rows: [
      { feature: 'Live Front Page',             status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'Article Display',             status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'Feed Resolution',             status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'User Books',                  status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'PDF Generation',              status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
      { feature: 'Article Search',              status: 'done',         ui: 'Complete',  backend: 'Ready for Customer Review',  backendLink: DOCS_BASE_URL },
    ],
  },
]

// ─── Milestones ───────────────────────────────────────────────────────────────

interface MilestoneItem {
  label: string
  pct: number  // 0–100
}

interface Milestone {
  name: string
  items: MilestoneItem[]
}

const MILESTONES: Milestone[] = [
  {
    name: 'Milestone 1',
    items: [
      { label: 'Technical Contract Package v0 Published', pct: 100 },
      { label: 'API Contract',     pct: 100 },
      { label: 'Event Schema',     pct: 100 },
      { label: 'Article Schema',   pct: 100 },
      { label: 'Sample Payloads',  pct: 100 },
    ],
  },
  {
    name: 'Milestone 2',
    items: [
      { label: 'Sample Production Workflows',            pct: 100 },
      { label: 'API returns actual articles (no mocks)', pct: 100 },
      { label: 'UI shows contract-driven articles',      pct: 100 },
    ],
  },
  {
    name: 'Milestone 3',
    items: [
      { label: 'AuthN integration for UI tier',                                                          pct: 100 },
      { label: 'ABAC enforced at block level elements for articles',                                     pct: 100 },
      { label: 'AuthN validation, AuthZ RBAC & ABAC enforcement by API tier (decision + token caching)', pct: 100 },
      { label: 'AI integration into API tier and UI',                                                    pct: 100 },
    ],
  },
  {
    name: 'Milestone 4',
    items: [
      { label: 'Event-driven metrics and dashboard',              pct: 100 },
      { label: 'Logging events emitted',                         pct: 100 },
      { label: 'Consumer aggregates computed',                   pct: 100 },
      { label: 'Dashboard consistent with article engagement',   pct: 100 },
    ],
  },
]

// ─── Components ───────────────────────────────────────────────────────────────

function StatusIcon({ status, size = 14 }: { status: FeatureStatus; size?: number }) {
  if (status === 'done')
    return <CheckCircle size={size} className="text-teal-9 shrink-0" aria-label="Complete" />
  if (status === 'in-progress')
    return <CircleDashed size={size} className="text-amber-11 shrink-0" aria-label="In progress" />
  return <Circle size={size} className="text-fg-secondary shrink-0" aria-label="Not started" />
}

// ─── Attention cell ───────────────────────────────────────────────────────────

const ATTENTION_STATUSES = ['Ready for Customer Review', 'Functional Draft', 'API Design Reviewable']

function StatusCell({ label, link }: { label: string; link?: string }) {
  const isAttention = ATTENTION_STATUSES.includes(label)

  if (isAttention && link) {
    const isExternal = link.startsWith('http')
    const linkedLabel = isExternal
      ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline underline-offset-2 hover:text-blue-800">{label}</a>
      : <Link to={link} className="text-blue-600 underline underline-offset-2 hover:text-blue-800">{label}</Link>
    return (
      <span className="inline-flex items-center gap-1 font-semibold">
        <Zap size={11} className="shrink-0 fill-amber-11" aria-hidden="true" />
        {linkedLabel}
      </span>
    )
  }

  return (
    <span className={`inline-flex items-center gap-1 ${isAttention ? 'font-semibold text-amber-11' : 'text-fg-secondary'}`}>
      {isAttention && <Zap size={11} className="shrink-0 fill-amber-11" aria-hidden="true" />}
      {label}
    </span>
  )
}

function pctToStatus(pct: number): FeatureStatus {
  if (pct >= 100) return 'done'
  if (pct > 0) return 'in-progress'
  return 'not-started'
}

function MilestoneCard({ milestone }: { milestone: Milestone }) {
  const total = milestone.items.length
  const overall = total > 0 ? Math.round(milestone.items.reduce((sum, i) => sum + i.pct, 0) / total) : 0
  const overallStatus = pctToStatus(overall)

  return (
    <div className="border-b border-line-subtle pb-4 mb-4 last:border-0 last:mb-0 last:pb-0">
      <div className="flex items-center gap-2 mb-2">
        <StatusIcon status={overallStatus} size={13} />
        <span className="font-zodiak text-sm font-bold text-fg leading-tight">{milestone.name}</span>
        <span className="ml-auto font-sans text-[10px] text-fg-secondary tabular-nums shrink-0">{overall}%</span>
      </div>

      {/* Progress bar */}
      <div className="h-1 w-full bg-fill rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-teal-9 transition-all"
          style={{ width: `${overall}%` }}
        />
      </div>

      {/* Item list */}
      <ul className="space-y-1">
        {milestone.items.map((item) => (
          <li key={item.label} className="flex items-center gap-2">
            <StatusIcon status={pctToStatus(item.pct)} size={11} />
            <span className="font-sans text-[11px] text-fg-secondary flex-1">{item.label}</span>
            <span className="font-sans text-[10px] text-fg-secondary tabular-nums">{item.pct}%</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function DevStatusPage() {
  return (
    <div className="max-w-7xl mx-auto px-8 py-10">
      {/* Newspaper-style section header */}
      <div className="mb-6 text-center border-b-2 border-fg pb-4">
        <p className="font-sans text-[9px] font-semibold uppercase tracking-[0.25em] text-fg-secondary mb-1">
          Internal Development Edition
        </p>
        <h1 className="font-zodiak text-3xl font-bold text-fg">
          Platform Status Index
        </h1>
        <p className="font-sans text-[11px] text-fg-secondary mt-1">
          Current build state across UI and API layers —{' '}
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* Two-column body */}
      <div className="flex gap-10 items-start">

        {/* ── Left: feature status table ── */}
        <div className="flex-1 min-w-0">
          <table className="w-full border-collapse font-sans text-xs">
            <thead>
              <tr className="border-b-2 border-fg">
                <th className="py-2 pr-4 text-left font-semibold uppercase tracking-wider text-fg-secondary w-6">
                  <span className="sr-only">Status</span>
                </th>
                <th className="py-2 pr-8 text-left font-semibold uppercase tracking-wider text-fg-secondary">
                  Feature
                </th>
                <th className="py-2 pr-8 text-left font-semibold uppercase tracking-wider text-fg-secondary border-l border-line pl-4">
                  Front End
                </th>
                <th className="py-2 text-left font-semibold uppercase tracking-wider text-fg-secondary border-l border-line pl-4">
                  Back End
                </th>
              </tr>
            </thead>
            <tbody>
              {FEATURE_GROUPS.map((group) => (
                <>
                  <tr key={group.group} className="border-t border-b border-line bg-fill">
                    <td colSpan={4} className="py-1.5 px-0">
                      <span className="font-sans text-[9px] font-bold uppercase tracking-[0.2em] text-fg-secondary">
                        {group.group}
                      </span>
                    </td>
                  </tr>
                  {group.rows.map((row) => (
                    <tr key={row.feature} className="border-b border-line-subtle hover:bg-fill transition-colors">
                      <td className="py-2.5 pr-4">
                        <StatusIcon status={row.status} />
                      </td>
                      <td className="py-2.5 pr-8 font-zodiak text-sm font-bold text-fg">
                        {row.feature}
                      </td>
                      <td className="py-2.5 pr-8 text-fg-secondary border-l border-line-subtle pl-4">
                        <StatusCell label={row.ui} link={row.uiLink} />
                      </td>
                      <td className="py-2.5 text-fg-secondary border-l border-line-subtle pl-4">
                        <StatusCell label={row.backend} link={row.backendLink} />
                      </td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>

          {/* Legend */}
          <div className="mt-5 flex gap-6 border-t border-line-subtle pt-4">
            <div className="flex items-center gap-1.5 text-[10px] text-fg-secondary font-sans">
              <CheckCircle size={11} className="text-teal-9" /> Complete
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-fg-secondary font-sans">
              <CircleDashed size={11} className="text-amber-11" /> In Progress
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-fg-secondary font-sans">
              <Circle size={11} className="text-fg-secondary" /> Not Started
            </div>
          </div>
        </div>

        {/* ── Right: milestones ── */}
        <div className="w-72 shrink-0">
          <div className="border-b-2 border-fg py-2 mb-4">
            <p className="font-sans text-xs font-semibold uppercase tracking-wider text-fg-secondary">
              Project Milestones
            </p>
          </div>
          <div className="mb-4 rounded border border-warning-line bg-warning-fill px-3 py-2.5 text-[10px] leading-relaxed text-warning-fg">
            <span className="font-bold uppercase tracking-wide">Note: </span>
            These estimates are for dev-team reference only and do not reflect official project management communication or official customer acceptance.
          </div>
          {MILESTONES.map((m) => (
            <MilestoneCard key={m.name} milestone={m} />
          ))}
        </div>

      </div>
    </div>
  )
}

